import React, { useState } from 'react';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Form, Formik, FormikValues } from 'formik';

import { getChangedValues } from '~libs/dataUtils';
import generateYupSchema from '~libs/yupValidation';
import { onChangeTime } from '~appointments/lib/onchange';
import { validateStartEndTimes, validateUserAvailability } from '~appointments/lib/validate';
import { Appointment } from '~weekly-planner/lib/common';

import Button from '~components/Button';
import { FieldProps, Fields } from '~components/form-fields';
import { AlertMessage } from '~components/form-fields/inputs';
import Modal from '~components/Modal';
import FormInputDate from '~appointments/components/FormInputDate';
import FormInputStartEndTimes from '~appointments/components/FormInputStartEndTimes';
import FormInputWorkerNotes from '~appointments/components/FormInputWorkerNotes';

interface ComponentProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  appointment: Appointment | null;
  onSave: (values: Partial<Record<string, any>>) => Promise<void>;
}

const fields: FieldProps = {
  id: {
    type: 'hidden',
    field: 'id',
  },
  name: {
    type: 'readonly',
    field: 'client.full_name',
    caption: 'Name',
  },
  service_type: {
    type: 'readonly',
    field: 'service_type.name',
    caption: 'Service Type',
  },
  date: {
    type: 'custom',
    component: FormInputDate,
    caption: 'Appointment Date',
    required: true,
    colSize: 4,
    order: 100,
  },
  start_end_time: {
    type: 'custom',
    caption: 'Start/End Times',
    colSize: 9,
    component: FormInputStartEndTimes,
    dependencies: ['date', 'start_time', 'end_time', 'ends_next_day'],
    validateConfirm: validateUserAvailability,
    validate: validateStartEndTimes,
    onChange: onChangeTime,
    order: 200,
  },
  notes: {
    type: 'custom',
    component: FormInputWorkerNotes,
    caption: 'Rostering Notes',
    order: 300,
    setFieldValue: true,
    defaultValue: '',
    isAttribute: false,
  },
};

const UnallocatedAppointmentEditModal: React.FC<ComponentProps> = ({ isOpen, setIsOpen, appointment, onSave }) => {
  const fieldsObject: FieldProps = fields;
  const fieldDefinitions = Object.entries(fieldsObject).map(([key, props]) => ({ key, ...props }));

  const initialValues = appointment ? { ...appointment } : {};

  const validationSchema = generateYupSchema(fieldsObject);

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleOnSubmit = async (
    values: FormikValues,
    {
      setSubmitting,
      setStatus,
    }: { setSubmitting: (isSubmitting: boolean) => void; setStatus: (status: boolean | undefined) => void },
  ) => {
    setHasSubmitted(true);
    try {
      setSubmitting(true);
      await onSave(getChangedValues(values, initialValues));
      setIsOpen(false);
      setStatus(undefined);
    } catch (err: any) {
      setStatus(err?.message ?? 'Failed to save changes to the appointment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      setIsOpen={setIsOpen}
      toggle={() => setIsOpen(!isOpen)}
      className={'quick-edit'}
      bodyClassName={'p-0'}
      includeFooter={false}
    >
      <ModalHeader className="header">Quick Edit</ModalHeader>
      <ModalBody>
        <div className="p-2">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnMount={false}
            // Re-validate on change only once the user has hit submit — this clears stale
            // invalid-field highlights as soon as they fix the offending field, while staying
            // quiet on untouched fields before the first submit attempt.
            validateOnChange={hasSubmitted}
            validateOnBlur={false}
            onSubmit={handleOnSubmit}
          >
            {({ values, setFieldValue, setFieldTouched, touched, isSubmitting, errors, status }) => (
              <Form>
                <div className="d-flex w-100">
                  <div>
                    <Fields
                      definitions={fieldDefinitions}
                      row={appointment ?? ({} as any)}
                      invalidFields={Object.keys(errors ?? {})}
                      values={values}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      touched={touched}
                    />
                  </div>
                </div>
                <div className="d-flex w-100">
                  <AlertMessage
                    id={'edit-unallocated-alert'}
                    name={'Edit Unallocated Appointment Alert'}
                    color={'warning'}
                    value={
                      'Please note, this will save your appointment immediately. It will not be an unsaved change.'
                    }
                    type={'alert-warning'}
                  />
                </div>
                {status && (
                  <div className="d-flex w-100">
                    <AlertMessage
                      id="edit-unallocated-error"
                      name="Edit Unallocated Appointment Error"
                      color="danger"
                      value={status}
                      type="alert-danger"
                    />
                  </div>
                )}
                <ModalFooter>
                  <Button type="submit" disabled={isSubmitting} color="success">
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                  <Button color="secondary" onClick={() => setIsOpen(!isOpen)}>
                    Close
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default UnallocatedAppointmentEditModal;
